// ...imports...
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BloqueioAgendaPostModel, TipoBloqueioAgenda } from 'app/models/bloqueio-agenda.model';
import { BloqueioAgendaService } from 'app/services/bloqueio-agenda.service';
import { NotificacaoService } from 'app/services/notificacao.service';
import { SweetAlertService } from 'app/services/sweetalert.service';

@Component({
    selector: 'app-bloqueio-agenda-form',
    templateUrl: './bloqueio-agenda-form.component.html',
    styleUrls: ['./bloqueio-agenda-form.component.css'],
    standalone: false
})
export class BloqueioAgendaFormComponent implements OnInit {
    @Input() bloqueioId: number = 0;
    @Input() dataInicialSugerida?: Date;

    loading = false;
    salvando = false;
    excluindo = false;
        // cancelar() já está implementado mais abaixo, removido duplicado aqui
        async excluir(): Promise<void> {
            if (!this.bloqueioId || this.bloqueioId === 0) return;
            const resultado = await this.sweetAlertService.alertaRemover();
            if (!resultado?.isConfirmed) return;
            this.excluindo = true;
            try {
                const resp = await this.bloqueioService.remover(this.bloqueioId);
                this.notificacaoService.mensagemSucesso(resp.mensagem || 'Bloqueio excluído com sucesso');
                this.activeModal.close(true);
            } catch (err: any) {
                this.notificacaoService.mensagemErro(
                    err?.error?.mensagem || err?.error?.message || 'Erro ao excluir bloqueio'
                );
            } finally {
                this.excluindo = false;
            }
        }
    form: FormGroup;

    TipoEnum = TipoBloqueioAgenda;
    tipos = [
        { id: TipoBloqueioAgenda.Pontual, nome: 'Bloqueio pontual (data/hora)' },
        { id: TipoBloqueioAgenda.DiaInteiro, nome: 'Dia inteiro' },
        { id: TipoBloqueioAgenda.Periodo, nome: 'Período (intervalo de dias)' },
        { id: TipoBloqueioAgenda.Recorrente, nome: 'Recorrente semanal' }
    ];
    diasSemana = [
        { id: 0, nome: 'Domingo' },
        { id: 1, nome: 'Segunda-feira' },
        { id: 2, nome: 'Terça-feira' },
        { id: 3, nome: 'Quarta-feira' },
        { id: 4, nome: 'Quinta-feira' },
        { id: 5, nome: 'Sexta-feira' },
        { id: 6, nome: 'Sábado' }
    ];

    constructor(
        private activeModal: NgbActiveModal,
        private formBuilder: FormBuilder,
        private bloqueioService: BloqueioAgendaService,
        private notificacaoService: NotificacaoService,
        private sweetAlertService: SweetAlertService
    ) {
        this.form = this.formBuilder.group({
            id: [0],
            tipo: [TipoBloqueioAgenda.Pontual, [Validators.required]],
            data: [null],
            horaInicio: [null],
            horaFim: [null],
            dataInicio: [null],
            dataFim: [null],
            diaSemanaRecorrente: [null],
            horaInicioRecorrente: [null],
            horaFimRecorrente: [null],
            vigenciaInicio: [null],
            vigenciaFim: [null],
            motivo: [null, [Validators.maxLength(200)]]
        });
    }

    async ngOnInit(): Promise<void> {
        this.loading = true;
        try {
            if (this.bloqueioId && this.bloqueioId !== 0) {
                await this.carregar();
            } else if (this.dataInicialSugerida) {
                const d = new Date(this.dataInicialSugerida);
                d.setHours(0, 0, 0, 0);
                this.form.patchValue({
                    data: d,
                    dataInicio: d
                });
            }
        } finally {
            this.loading = false;
        }
    }

    private async carregar(): Promise<void> {
        const b = await this.bloqueioService.obter(this.bloqueioId);
        const ini = new Date(b.dataInicio);
        const fim = new Date(b.dataFim);

        this.form.patchValue({
            id: b.id,
            tipo: b.tipo,
            motivo: b.motivo
        });

        switch (b.tipo) {
            case TipoBloqueioAgenda.Pontual:
                this.form.patchValue({
                    data: this.toMidnight(ini),
                    horaInicio: this.toTimeInput(ini),
                    horaFim: this.toTimeInput(fim)
                });
                break;
            case TipoBloqueioAgenda.DiaInteiro:
                this.form.patchValue({ data: this.toMidnight(ini) });
                break;
            case TipoBloqueioAgenda.Periodo:
                this.form.patchValue({
                    dataInicio: this.toMidnight(ini),
                    dataFim: this.toMidnight(fim)
                });
                break;
            case TipoBloqueioAgenda.Recorrente:
                this.form.patchValue({
                    diaSemanaRecorrente: b.diaSemanaRecorrente,
                    horaInicioRecorrente: b.horaInicioRecorrente,
                    horaFimRecorrente: b.horaFimRecorrente,
                    vigenciaInicio: this.toMidnight(ini),
                    vigenciaFim: fim.getFullYear() < 9000 ? this.toMidnight(fim) : null
                });
                break;
        }
    }

    private toMidnight(d: Date): Date {
        const r = new Date(d);
        r.setHours(0, 0, 0, 0);
        return r;
    }

    private formatDate(d: Date): string {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    private toTimeInput(d: Date): string {
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
    }

    cancelar(): void {
        this.activeModal.close(false);
    }

    async salvar(): Promise<void> {
        const v = this.form.value;
        const tipo: TipoBloqueioAgenda = +v.tipo;

        const validacao = this.validarPorTipo(tipo, v);
        if (validacao) {
            this.notificacaoService.mensagemErro(validacao);
            return;
        }

        const model: BloqueioAgendaPostModel = this.montarModel(tipo, v);

        this.salvando = true;
        try {
            const resp = !this.bloqueioId || this.bloqueioId === 0
                ? await this.bloqueioService.adicionar(model)
                : await this.bloqueioService.atualizar(model);
            this.notificacaoService.mensagemSucesso(resp.mensagem);
            this.activeModal.close(true);
        } catch (err: any) {
            this.notificacaoService.mensagemErro(
                err?.error?.mensagem || err?.error?.message || 'Erro ao salvar bloqueio'
            );
        } finally {
            this.salvando = false;
        }
    }

    private validarPorTipo(tipo: TipoBloqueioAgenda, v: any): string | null {
        switch (tipo) {
            case TipoBloqueioAgenda.Pontual:
                if (!v.data || !v.horaInicio || !v.horaFim) return 'Preencha data, hora de início e fim.';
                if (v.horaFim <= v.horaInicio) return 'Hora de fim deve ser maior que a de início.';
                return null;
            case TipoBloqueioAgenda.DiaInteiro:
                if (!v.data) return 'Selecione a data do bloqueio.';
                return null;
            case TipoBloqueioAgenda.Periodo:
                if (!v.dataInicio || !v.dataFim) return 'Preencha data inicial e final.';
                if (new Date(v.dataFim) < new Date(v.dataInicio)) return 'Data final deve ser maior ou igual à inicial.';
                return null;
            case TipoBloqueioAgenda.Recorrente:
                if (v.diaSemanaRecorrente === null || v.diaSemanaRecorrente === undefined)
                    return 'Selecione o dia da semana.';
                if (!v.horaInicioRecorrente || !v.horaFimRecorrente)
                    return 'Preencha os horários de início e fim.';
                if (v.horaFimRecorrente <= v.horaInicioRecorrente)
                    return 'Hora de fim deve ser maior que a de início.';
                return null;
        }
        return null;
    }

    private montarModel(tipo: TipoBloqueioAgenda, v: any): BloqueioAgendaPostModel {
        const base: BloqueioAgendaPostModel = {
            id: v.id || 0,
            tipo: tipo,
            dataInicio: '' as any,
            dataFim: '' as any,
            motivo: v.motivo || undefined
        };

        switch (tipo) {
            case TipoBloqueioAgenda.Pontual: {
                const data = this.formatDate(v.data);
                base.dataInicio = `${data}T${v.horaInicio}:00`;
                base.dataFim = `${data}T${v.horaFim}:00`;
                break;
            }
            case TipoBloqueioAgenda.DiaInteiro: {
                const data = this.formatDate(v.data);
                base.dataInicio = `${data}T00:00:00`;
                base.dataFim = `${data}T23:59:59`;
                break;
            }
            case TipoBloqueioAgenda.Periodo:
                base.dataInicio = `${this.formatDate(v.dataInicio)}T00:00:00`;
                base.dataFim = `${this.formatDate(v.dataFim)}T23:59:59`;
                break;
            case TipoBloqueioAgenda.Recorrente:
                base.dataInicio = v.vigenciaInicio
                    ? `${this.formatDate(v.vigenciaInicio)}T00:00:00`
                    : `${this.formatDate(new Date())}T00:00:00`;
                base.dataFim = v.vigenciaFim
                    ? `${this.formatDate(v.vigenciaFim)}T23:59:59`
                    : '9999-12-31T23:59:59';
                base.diaSemanaRecorrente = +v.diaSemanaRecorrente;
                base.horaInicioRecorrente = v.horaInicioRecorrente;
                base.horaFimRecorrente = v.horaFimRecorrente;
                break;
        }
        return base;
    }
}
